import styles from './reviews.scss';
import cn from 'classnames';
import { NO_ELEMENT, rm, hasElements } from 'utils';
import { StarRating } from 'components/Shared/StarRating';
import { isValidElement, useState } from 'react';
import { ProductTag } from 'components/Shared/Product';
import Image from 'components/Shared/Image';
import { RedReview } from 'models/RedReview';
import { ReviewResponse as ReviewResponseModel } from 'models/ReviewResponse';
import { intlFormatDistance, format } from 'date-fns';
import Modal from 'components/Shared/Modal';
import { ToolTip } from 'components/Shared/ToolTip';

const reviewAge = (date: Date, channel: string) => {
  const reviewDateNew = new Date(date);

  const locale = channel === 'BNO' ? 'no' : 'sv';

  const age = intlFormatDistance(reviewDateNew, new Date(), {
    locale: locale,
    numeric: 'always'
  });

  if (age.includes('kvartal')) {
    return intlFormatDistance(reviewDateNew, new Date(), {
      locale: locale,
      numeric: 'always',
      unit: 'month'
    });
  }

  return age;
};

const reviewDate = (date: Date) => {
  return format(new Date(date), 'yyyy-MM-dd');
};

const AuthorInfo = ({
  author,
  verifiedBuyer,
  date,
  isMobileLayout,
  score,
  channel
}: Pick<
  MergedProps,
  'score' | 'date' | 'author' | 'verifiedBuyer' | 'isMobileLayout' | 'channel'
>) => {
  return (
    <>
      <div className={styles.reviewInfoRow}>
        <StarRating score={score} />
      </div>
      <p className={styles.author}>{author}</p>
      {verifiedBuyer && <p className={styles.verifiedBuyer}>{rm('review_verified_buyer')}</p>}
      {!isMobileLayout && (
        <ToolTip
          text={reviewDate(date)}
          hoverText={reviewAge(date, channel)}
          skipDefaultStyle={true}
          className={styles.date}
        />
      )}
      {isMobileLayout && <span className={styles.date}>{reviewAge(date, channel)}</span>}
    </>
  );
};

const ProCon = ({
  text,
  iconClassName,
  icon
}: {
  text: string;
  iconClassName: string;
  icon: '+' | '-';
}) => (
  <p className={styles.zeroMargin}>
    <span className={cn(styles.proConIcon, iconClassName)}>{icon}</span>
    {text}
  </p>
);

const ProsAndCons = ({ pros, cons }: Pick<RedReview, 'pros' | 'cons'>) => (
  <div className={styles.proConsContainer}>
    {hasElements(pros) && (
      <div>
        {pros.map((pro, index) => (
          <ProCon key={index} text={pro} icon={'+'} iconClassName={styles.proIcon} />
        ))}
      </div>
    )}
    {hasElements(cons) && (
      <div>
        {cons.map((con, index) => (
          <ProCon key={index} text={con} icon={'-'} iconClassName={styles.conIcon} />
        ))}
      </div>
    )}
  </div>
);

const ReviewResponse = ({ comment, name }: Pick<ReviewResponseModel, 'name' | 'comment'>) => (
  <div className={styles.reviewResponse}>
    <p>{comment}</p>
    <p className={styles.bold}>{name}</p>
  </div>
);

interface MergedProps extends RedReview {
  isMostPopular?: boolean;
  button?: React.ReactElement;
  isMobileLayout?: boolean;
  channel: string;
}
export const Review = ({
  score,
  date,
  comment,
  author,
  pros,
  cons,
  isMostPopular,
  images,
  verifiedBuyer,
  button,
  response,
  isMobileLayout,
  channel
}: MergedProps) => {
  const iconImages = images.filter(image => image.type === 'icon');
  const mediumImages = images.filter(image => image.type === 'medium');

  const [showImageModal, setShowImageModal] = useState(false);
  const [imageNumber, setimageNumber] = useState(0);

  const openModal = (imageNumber: number) => {
    setimageNumber(imageNumber);
    setShowImageModal(true);
  };
  const closeModal = () => {
    setShowImageModal(false);
  };

  const isLandscape = (height: number, width: number) => {
    return height < width;
  };

  return (
    <div className={styles.reviewContainer}>
      <div className={styles.reviewInfo}>
        <AuthorInfo
          author={author}
          verifiedBuyer={verifiedBuyer}
          date={date}
          isMobileLayout={isMobileLayout}
          score={score}
          channel={channel}
        />
      </div>
      <div className={styles.reviewComment}>
        <p className={styles.comment}>{comment}</p>
        <ProsAndCons pros={pros} cons={cons} />
        {hasElements(iconImages) ? (
          <div className={styles.reviewImageContainer}>
            {iconImages.map((image, index) => (
              <Image
                className={styles.reviewImage}
                key={index}
                height={image.height}
                width={image.width}
                src={image.url}
                onClick={() => openModal(index)}
              />
            ))}

            <Modal
              contentClassName={cn({
                [styles.landscape]: isLandscape(
                  mediumImages[imageNumber].height,
                  mediumImages[imageNumber].width
                )
              })}
              onClose={closeModal}
              show={showImageModal}
            >
              <img src={mediumImages[imageNumber].url} />
            </Modal>
          </div>
        ) : (
          NO_ELEMENT
        )}
      </div>
      <div className={styles.helpfulButtonContainer}>
        {isMostPopular && !isMobileLayout && (
          <div className={styles.reviewTag}>
            <ProductTag containerClassName={styles.zeroMargin} type="MOST_POPULAR_REVIEW" />
          </div>
        )}
        {!isMobileLayout && (
          <div className={styles.reviewHelpfulButton}>
            {isValidElement(button) ? button : NO_ELEMENT}
          </div>
        )}
      </div>
      {isMobileLayout && (
        <div className={styles.reviewHelpfulButton}>
          {isValidElement(button) ? button : NO_ELEMENT}
        </div>
      )}
      {response ? <ReviewResponse comment={response.comment} name={response.name} /> : NO_ELEMENT}
    </div>
  );
};
